/**
 * GUMSHOE — typed event bus over EventMap. Frozen core.
 */
import type { EventBus, EventHandler, EventMap, EventName } from './types'

type AnyHandler = (payload: EventMap[EventName]) => void

export const createEventBus = (): EventBus => {
  const handlers = new Map<EventName, Set<AnyHandler>>()

  const on = <K extends EventName>(event: K, handler: EventHandler<K>): (() => void) => {
    const set = handlers.get(event) ?? new Set<AnyHandler>()
    set.add(handler as AnyHandler)
    handlers.set(event, set)
    return () => off(event, handler)
  }

  const off = <K extends EventName>(event: K, handler: EventHandler<K>): void => {
    handlers.get(event)?.delete(handler as AnyHandler)
  }

  const emit = <K extends EventName>(event: K, payload: EventMap[K]): void => {
    const set = handlers.get(event)
    if (!set) return
    // Snapshot so handlers may unsubscribe during dispatch.
    for (const handler of [...set]) {
      handler(payload)
    }
  }

  return { on, off, emit }
}
